import type { InvoiceWhere } from "@tillix/db/validators/InvoiceWhere";
import { computed } from "mobx";
import {
  _await,
  ExtendedModel,
  modelAction,
  modelFlow,
  prop,
  type Ref,
} from "mobx-keystone";
import { api } from "../../../orpc/client";
import { findNextDue } from "../../lib/compare-by-date";
import { createTStore, type TStoreInit } from "../../lib/models/create-t-store";
import { addToRefArray, removeFromRefArray } from "../../lib/models/ref";
import { TStore } from "../../lib/models/t-store";
import { InvoiceModel } from "./model.invoice";
import { invoiceNS } from "./ns.invoice";
import type { Self } from "../../lib/models/ref";
import type { AnyStore } from "../../lib/models/t-store";

const SELECTED_FOR_PAYMENT_KEY = "selectedForPayment" as const;

type InvoiceWhereInput = typeof InvoiceWhere.infer;

@invoiceNS.store
export class InvoiceStore extends ExtendedModel(
  TStore<InvoiceModel, InvoiceWhereInput, typeof api.invoice>,
  {
    selectedForPayment: prop<Ref<InvoiceModel>[]>(() => []),
  },
) {
  Model = InvoiceModel;
  api = api.invoice;

  @computed
  get selectedForPaymentModels(): InvoiceModel[] {
    return this.selectedForPayment.map((ref) => ref.current);
  }

  @computed
  get unpaidInvoices(): InvoiceModel[] {
    return this.data.filter((inv) => inv.amountOpen > 0);
  }

  @computed
  get openInvoices(): InvoiceModel[] {
    return this.unpaidInvoices.filter((inv) => inv.isSelectableForPayment);
  }

  @computed
  get nextDueInvoice(): InvoiceModel | undefined {
    return findNextDue(this.unpaidInvoices);
  }

  @computed
  get areAllOpenInvoicesSelectedForPayment(): boolean {
    const open = this.openInvoices;
    return open.length > 0 && open.every((inv) => inv.isSelectedForPayment);
  }

  @computed
  get areAnyOpenInvoicesSelectedForPayment(): boolean {
    return this.openInvoices.some((inv) => inv.isSelectedForPayment);
  }

  @modelAction
  selectForPayment(
    item: InvoiceModel["id"] | InvoiceModel["$modelId"] | InvoiceModel,
  ): boolean {
    return (
      addToRefArray(
        this as unknown as AnyStore,
        SELECTED_FOR_PAYMENT_KEY,
        item,
      ) ?? false
    );
  }

  @modelAction
  deselectForPayment(
    item: InvoiceModel["id"] | InvoiceModel["$modelId"] | InvoiceModel,
  ): boolean {
    return (
      removeFromRefArray(
        this as unknown as AnyStore,
        SELECTED_FOR_PAYMENT_KEY,
        item,
      ) ?? false
    );
  }

  protected _sync(): Promise<void> {
    throw new Error("Method not implemented.");
  }

  @modelFlow
  public *syncById(this: InvoiceStore, id: string) {
    const response = yield* _await(this.api.get({ where: { id } }));

    const [invoice] = response.items;

    if (!invoice) throw new Error("Invoice not found");

    const invoiceModel = this._data.find((inv) => inv.id === invoice.id);

    if (!invoiceModel) {
      // this.addRaw([invoice]);
    }

    return {};
  }

  updateById(id: string, data: Self<InvoiceModel>): Promise<{}> {
    throw new Error("Method not used in this store.");
  }
}

export const createInvoiceStore = (
  incomingData?: TStoreInit<InvoiceModel, InvoiceStore>,
) => createTStore<InvoiceModel, InvoiceStore>(InvoiceStore, incomingData);
